import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { groupBy, partition } from 'lodash';
import {
  layerTypesWithShortcuts,
  mapModes,
  SOIL_BOUNDARIES_LAYER_ID,
} from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import { getPaintColor } from '../../utils/colors';
import { PanelSection } from '../SidePanel';
import {
  HiddenButton,
  HoverNavItem,
  IconSpan,
  MaxWidthNameInput,
  NavMainItem,
  StickyHeader,
} from './menu-components';
import { useTilesetContext } from '../../contexts/TilesetContext';
import { SUITABILITY_LAYER_ID } from '../right-menu/SuitabilityMenu';
import LayersIcon from '../icons/LayersIcon';
import SuitabilityInfoTooltip from './SuitabilityInfoTooltip';
import ChevronIcon from '../icons/ChevronIcon';
import { UnstyledButton } from '../basics/UnstyledButton';
import { flyTo } from '../../utils/map';
import { ItemContext } from '../../contexts/ItemContext';
import { useIsMobile } from '../../utils/responsive';
import PuzzleIcon from '../icons/PuzzleIcon';
import ColoredSpan from '../basics/ColoredSpan';
import { BASE_LAYER_ID } from '../right-menu/BaseLayerMenu';
import PictureIcon from '../icons/PictureIcon';
import ButtonWithShortcut from '../basics/ButtonWithShortcut';
import { SHORTCUTS } from '../../constants/shortcuts';
import Shortcut from '../basics/Shortcut';
import NewLayerButton, {
  useSwitchToLayer,
} from '../map-toolbars/buttons/NewLayerButton';
import { layerHasData, layerIsCustom } from '../../utils/layers';
import { captureEvent } from '../../utils/analytics';
import { useToggleMap } from '../../hooks/useToggleMap';
import { LAND_USE_LAYER_ID } from '../right-menu/LandUseLayerMenu';
import { CLIMATE_LAYER_ID } from '../right-menu/ClimateLayerMenu';
import HistoryIcon from '../icons/HistoryIcon';
import CloudIcon from '../icons/CloudIcon';
import {
  isClimateTileset,
  isLandUseTileset,
  isSuitabilityTileset,
} from '../../utils/tilesets';
import { MultiSelectLayerFeatureCheckbox } from './MultiSelectCheckbox';
import { useUserCanEditFarm } from '../../utils/hooks';
import ShowHideLayersButton from './ShowHideLayersButton';

const LayerFeatureItem = ({
  feature,
  featuresShown,
  iconComponent,
  layerShown,
  name,
  nameIsEditable,
}) => {
  const {
    layers: { setLayerFeatureProperty, toggleLayerFeature },
    mapState: {
      setMapMode,
      activeLayerFeatureId,
      mapRef,
      selectedLayerFeatureIds,
    },
  } = useContext(MapContext);
  const { setActiveKey, clearActiveKey, activeKey } = useContext(ItemContext);
  const { farmId } = useParams();
  const userCanEditFarm = useUserCanEditFarm(farmId);
  const { id: featureId } = feature.properties;
  const shown = layerShown && featuresShown[featureId];
  const color = shown ? 'var(--text-color)' : 'var(--text-muted-color)';
  const onFeatureClick = (e) => {
    flyTo(feature, mapRef);
    clearActiveKey();
    if (e.metaKey || e.ctrlKey) {
      setMapMode(mapModes.MULTI_LAYER_FEATURE_SELECT, featureId, {
        selectActiveLayerFeature: true,
      });
    } else {
      setMapMode(mapModes.LAYER_FEATURE_ENGAGED, featureId);
    }
  };
  const onClickShowHide = () => toggleLayerFeature(featureId);
  return (
    <HoverNavItem
      key={featureId}
      onClick={onFeatureClick}
      isActive={
        selectedLayerFeatureIds.has(featureId) ||
        activeLayerFeatureId === featureId
      }
      onMouseEnter={() => setActiveKey(featureId)}
      onMouseLeave={clearActiveKey}
      hovered={activeKey === featureId}
    >
      <ColoredSpan color={color}>
        <IconSpan className="ml-4 d-flex justify-content-center">
          <MultiSelectLayerFeatureCheckbox
            checkboxEnabled={userCanEditFarm}
            id={featureId}
            isHovered={activeKey === featureId}
            onSelected={() => flyTo(feature, mapRef)}
            renderDefault={() => iconComponent}
          />
        </IconSpan>
      </ColoredSpan>
      <MaxWidthNameInput
        isEditable={nameIsEditable}
        maxWidth="153px"
        name={name}
        renderDefault={() => <ColoredSpan color={color}>{name}</ColoredSpan>}
        update={(value) =>
          setLayerFeatureProperty(featureId, farmId, 'name', value)
        }
      />
      {layerShown && (
        <HiddenButton
          className="pl-1 ml-auto font-small text-muted"
          onClick={(e) => {
            e.stopPropagation();
            onClickShowHide();
          }}
        >
          {shown ? 'hide' : 'show'}
        </HiddenButton>
      )}
    </HoverNavItem>
  );
};

const LayerNavItem = ({
  layer,
  color,
  onClickShowHide,
  onArrowClick,
  open,
  ...props
}) => {
  const {
    layerId,
    displayName,
    featuresShown,
    shown,
    data,
    iconComponent,
  } = layer;
  const {
    mapState: {
      setMapMode,
      activeLayerId,
      activeLayerFeatureId,
      selectedLayerFeatureIds,
    },
    layers: { renameCustomLayer },
  } = useContext(MapContext);
  const { farmId } = useParams();

  const isCustom = layerIsCustom(layer);
  const renameLayer = async (value) => {
    captureEvent('custom-layer-rename');
    await renameCustomLayer({ layer, farmId, displayName: value });
  };

  // If there's a shortcut for this layer, use it
  const shortcut = SHORTCUTS[layerId]?.key;

  const NavItemComponent = shortcut
    ? (p) => <ButtonWithShortcut {...p} as={HoverNavItem} shortcut={shortcut} />
    : HoverNavItem;

  // Text color can't be white since background is white
  const textColor = shown
    ? color === '#ffffff'
      ? '#bbbbbb'
      : color
    : 'var(--text-muted-color)';

  return (
    <>
      <NavItemComponent
        onClick={() => setMapMode(mapModes.LAYER_ENGAGED, layerId)}
        isActive={
          activeLayerId === layerId &&
          !activeLayerFeatureId &&
          !selectedLayerFeatureIds.size
        }
        {...props}
      >
        <IconSpan width="11px">
          {/* Only show layer segments for layers that can be drawn (not for contours) */}
          {(data?.features.length || 0) > 0 && layer.drawTool && (
            <UnstyledButton onClick={onArrowClick}>
              <ChevronIcon
                direction={open ? 'down' : 'right'}
                className="w-100"
              />
            </UnstyledButton>
          )}
        </IconSpan>
        <ColoredSpan color={textColor}>
          <IconSpan className="justify-content-center">
            {layerId === SUITABILITY_LAYER_ID ? (
              <PuzzleIcon />
            ) : layerId === BASE_LAYER_ID ? (
              <PictureIcon />
            ) : layerId === LAND_USE_LAYER_ID ? (
              <HistoryIcon />
            ) : layerId === CLIMATE_LAYER_ID ? (
              <CloudIcon />
            ) : (
              iconComponent
            )}
          </IconSpan>
        </ColoredSpan>
        <MaxWidthNameInput
          isEditable={isCustom}
          maxWidth="165px"
          name={displayName}
          renderDefault={() => (
            <ColoredSpan
              color={textColor}
              className={shown ? 'font-weight-bold' : ''}
            >
              {displayName}
            </ColoredSpan>
          )}
          update={renameLayer}
        />
        {layerId !== BASE_LAYER_ID && (
          <HiddenButton
            className="pl-1 ml-auto font-small text-muted"
            onClick={(e) => {
              e.stopPropagation();
              onClickShowHide();
            }}
          >
            {shown ? 'hide' : 'show'}
          </HiddenButton>
        )}
      </NavItemComponent>
      {open &&
        // Only show layer segments for layers that can be drawn (not for contours)
        layer.drawTool &&
        data.features.map((feature, i) => (
          <LayerFeatureItem
            key={feature.properties.id}
            name={feature.properties.name || `Feature ${i + 1}`}
            feature={feature}
            featuresShown={featuresShown}
            iconComponent={iconComponent}
            layerShown={shown}
            nameIsEditable={!layer.disableUserEdits}
          />
        ))}
    </>
  );
};

const INIT_LAYERS_OPEN_STATE = {};

const LayersTree = ({ addLayerEnabled }) => {
  const isMobile = useIsMobile();
  const {
    mapState: { activeLayerId, activeLayerFeatureId },
    layers: { sortedLayers, toggleLayer },
  } = useContext(MapContext);
  const switchToLayer = useSwitchToLayer();

  const { state: openState, toggle: toggleOpen } = useToggleMap(
    INIT_LAYERS_OPEN_STATE,
  );

  const { tileset, tilesetShown, setTilesetShown } = useTilesetContext();
  const [suitabilityShown, setSuitabilityShown] = useState(tilesetShown);
  const [landUseShown, setLandUseShown] = useState(tilesetShown);
  const [climateShown, setClimateShown] = useState(tilesetShown);

  // Toggle the layer open if it isn't already open when a feature is selected
  useEffect(() => {
    if (activeLayerFeatureId && !openState[activeLayerId]) {
      toggleOpen(activeLayerId);
    }
  }, [activeLayerFeatureId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [layersWithData, layersWithoutData] = useMemo(
    () =>
      partition(
        sortedLayers,
        (layer) =>
          layerHasData(layer) ||
          layer.layerId === activeLayerId ||
          layer.hasRemote ||
          layer.layerId === SOIL_BOUNDARIES_LAYER_ID,
      ),
    [activeLayerId, sortedLayers],
  );

  // Add shortcuts for layers with no data yet
  const layerIdsWithoutData = layersWithoutData.map(({ layerId }) => layerId);
  const layerIdsWithShortcuts = layerTypesWithShortcuts.filter((layerType) =>
    // Note: since `layerTypesWithShortcuts` are all non-custom layer types, layerdId === layerType
    layerIdsWithoutData.includes(layerType),
  );

  const toggleTilesetShown = (shown, setShown, isTileset) => () => {
    // If the current tileset matches the given function, show the tileset layer
    if (isTileset(tileset)) {
      setTilesetShown(!shown);
    }
    // Set the given shown state
    setShown(!shown);
  };

  // Handle menu shown on changes to tilesetShown
  useEffect(() => {
    if (!tilesetShown) return;
    if (isClimateTileset(tileset)) {
      setClimateShown(true);
    } else if (isLandUseTileset(tileset)) {
      setLandUseShown(true);
    } else if (isSuitabilityTileset(tileset)) {
      setSuitabilityShown(true);
    }
  }, [tileset, tilesetShown]);

  return (
    <PanelSection>
      <StickyHeader className="justify-content-between">
        <span className="d-flex align-items-center">
          <IconSpan>
            <LayersIcon />
          </IconSpan>
          Layers
        </span>
        <ShowHideLayersButton
          layers={layersWithData}
          setClimateShown={setClimateShown}
          setLandUseShown={setLandUseShown}
          setSuitabilityShown={setSuitabilityShown}
        />
      </StickyHeader>
      <ul>
        {Object.entries(groupBy(layersWithData, 'displayGroup')).map(
          ([displayGroup, groupedLayers]) => {
            return (
              <Fragment key={`${displayGroup}-layer-group`}>
                <NavMainItem
                  className="text-muted font-small font-weight-bold"
                  hoverable={false}
                >
                  {displayGroup === 'undefined' ? 'Custom' : displayGroup}
                </NavMainItem>
                {groupedLayers.map((layer) => {
                  const { layerId } = layer;
                  const color = getPaintColor(layer);
                  const onClickShowHide = () => {
                    toggleLayer(layerId);
                  };
                  const onArrowClick = (e) => {
                    e.stopPropagation();
                    toggleOpen(layerId);
                  };
                  return (
                    <LayerNavItem
                      key={layerId}
                      color={color}
                      layer={layer}
                      onArrowClick={onArrowClick}
                      onClickShowHide={onClickShowHide}
                      open={openState[layerId]}
                    />
                  );
                })}
              </Fragment>
            );
          },
        )}

        {layerIdsWithShortcuts.map((layerId) => (
          <Shortcut
            key={layerId}
            shortcut={SHORTCUTS[layerId].key}
            handler={() => switchToLayer(layerId)}
          />
        ))}

        <LayerNavItem
          id="SuitabilityLayerItem"
          layer={{
            layerId: SUITABILITY_LAYER_ID,
            displayName: 'Suitability (U.S.)',
            shown: suitabilityShown,
          }}
          color="#1AD209"
          onClickShowHide={toggleTilesetShown(
            suitabilityShown,
            setSuitabilityShown,
            isSuitabilityTileset,
          )}
        />
        {!isMobile && <SuitabilityInfoTooltip target="SuitabilityLayerItem" />}
        <LayerNavItem
          id="LandUseLayerItem"
          layer={{
            layerId: LAND_USE_LAYER_ID,
            displayName: 'Historical Land Use (U.S.)',
            shown: landUseShown,
          }}
          color="#212121"
          onClickShowHide={toggleTilesetShown(
            landUseShown,
            setLandUseShown,
            isLandUseTileset,
          )}
        />
        <LayerNavItem
          id="ClimateLayerItem"
          layer={{
            layerId: CLIMATE_LAYER_ID,
            displayName: 'Climate (U.S.)',
            shown: climateShown,
          }}
          color="#4277FF"
          onClickShowHide={toggleTilesetShown(
            climateShown,
            setClimateShown,
            isClimateTileset,
          )}
        />
        <LayerNavItem
          id="BaseLayerItem"
          layer={{
            layerId: BASE_LAYER_ID,
            displayName: 'Base Layer',
            shown: true,
          }}
          color="#8E8800"
        />
      </ul>
      {addLayerEnabled && <NewLayerButton color="light" className="ml-4 p-1" />}
    </PanelSection>
  );
};

export default LayersTree;
